export type PlatformOS = "ios" | "android" | "other";

/** The resolved rendering target: form factor first (desktop ≥md), then mobile OS. */
export type SurfacePlatform = "ios" | "android" | "desktop";

export interface PlatformEnvironment {
  userAgent: string;
  maxTouchPoints: number;
  /** `Capacitor.getPlatform()` when running inside the native shell. */
  capacitorPlatform?: string;
}

/**
 * The operating system, from the native shell when it can say so and from the
 * user agent otherwise. Pure so the heuristics below can be exercised without
 * a browser — see `platform.test.ts`.
 */
export function detectOS(env: PlatformEnvironment | null): PlatformOS {
  if (!env) return "other";

  // Capacitor knows the platform authoritatively when running inside the native shell.
  if (env.capacitorPlatform === "ios" || env.capacitorPlatform === "android") return env.capacitorPlatform;

  // iPadOS reports "MacIntel" but is the only Mac platform with multi-touch.
  if (/iPhone|iPad|iPod/.test(env.userAgent) || (/Mac/.test(env.userAgent) && env.maxTouchPoints > 1)) return "ios";
  if (/Android/.test(env.userAgent)) return "android";
  return "other";
}

/**
 * The surface skin to render. Form factor outranks OS — a desktop-width
 * viewport gets the detached card whatever the OS says — and an explicit
 * override outranks both (stories and tests pin a skin).
 */
export function resolveSurfacePlatform({
  override,
  isDesktop,
  os,
}: {
  override?: SurfacePlatform;
  isDesktop: boolean;
  os: PlatformOS;
}): SurfacePlatform {
  if (override) return override;
  if (isDesktop) return "desktop";
  return os === "ios" ? "ios" : "android";
}
